<template>
    <form
        v-if="panelType === 'select'"
        @submit.prevent="submitForm"
        class="d-flex flex-column form-container"
    >
        <!-- Audit Type -->
        <Field
            label="Type"
            v-model="localAudit.audit_type"
            type="radio"
            :required="true"
            :params="{
                options: [
                    { label: 'Audit', value: 'AUDIT' },
                    { label: 'Assessment', value: 'ASSESSMENT' },
                ],
                name: 'audit-type',
            }"
        />

        <!-- Template -->
        <Field
            label="Audit Template"
            v-model="localAudit.template_id"
            type="select"
            :required="true"
            :loading="isTemplateSelectionLoading"
            :params="{
                options: filteredAuditTypes,
                loadingText: templateSelectionLoadingText,
            }"
        />

        <!-- Date -->
        <Field
            label="Date"
            v-model="localAudit.date"
            type="date"
            :required="true"
            :params="{ maxDate: today }"
        />

        <!-- Auditor -->
        <Field
            label="Auditor"
            v-model="localAudit.auditor_id"
            :required="true"
            :disabled="!isAdminRoute"
            type="select"
            :params="{
                options: userOptions,
                placeholder: 'Select an auditor',
                searchable: true,
                allowEmpty: true,
            }"
        />

        <!-- Site -->
        <SiteDropdownField
            v-if="localAudit?.template_id"
            label="Site"
            type="select"
            v-model="localAudit.site_id"
            :required="true"
            :options="siteOptions"
            :disabled="isSiteFieldDisabled"
            :disabled-tooltip="disabledSiteTooltip"
            :placeholder="
                siteOptions.length > 0 ? 'Select a site' : 'No sites available'
            "
        >
            <span class="text-muted" style="font-size: 10pt">
                Only users assigned to this site may view this audit
            </span>
        </SiteDropdownField>

        <!-- Area -->
        <div v-if="localAudit?.template_id">
            <div class="area-container">
                <div style="flex: 1">
                    <Field
                        label="Area"
                        v-model="localAudit.area_id"
                        :required="true"
                        :disabled="areaOptions.length == 1"
                        type="select"
                        :params="{
                            options: areaOptions,
                            placeholder: 'Select an area',
                            searchable: true,
                            allowEmpty: true,
                        }"
                        style="width: 100%"
                    />
                </div>
                <button
                    v-if="
                        areaOptions.length === 0 ||
                        (localAudit?.template_id &&
                            (!auditAreas || auditAreas.length === 0))
                    "
                    type="button"
                    class="btn btn-link p-0 area-edit-link"
                    @click="openAreaForm"
                >
                    Edit
                </button>
            </div>

            <span
                v-if="auditAreas?.length > 0"
                class="text-muted"
                style="font-size: 10pt"
            >
                These are the areas added to this template
            </span>
        </div>

        <!-- Save Button -->
        <div
            style="
                padding-top: var(--space-6);
                border-top: 1px solid var(--neutral-600);
            "
        >
            <button
                type="submit"
                class="btn btn-secondary w-100"
                :disabled="isAuditStartDisabled"
            >
                <span
                    v-if="isLoadingAuditTemplate"
                    class="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                ></span>
                {{ isLoadingAuditTemplate ? 'Loading Template...' : 'Start' }}
            </button>
        </div>
    </form>
    <form v-else ref="auditForm" novalidate @submit.prevent="saveAudit">
        <div class="d-flex flex-column gap-3">
            <div class="audit-title">
                <img
                    v-if="localAudit.company_id == 0"
                    src="@/assets/images/logos/logomark.svg"
                    alt="IPAC Logo"
                    style="width: 30px; height: 30px"
                />
                <h3 style="color: var(--primary-500)">{{ localAudit.name }}</h3>
            </div>
            <p v-if="localAudit.description" class="text-muted m-0">
                {{ localAudit.description }}
            </p>
        </div>
        <div v-if="tabs.length > 1" class="sticky-header">
            <TabGroup v-model="activeTab" :tabs="tabs" />
        </div>

        <!--
            v-show must not be on the same node as d-flex: Bootstrap’s .d-flex
            uses display: flex !important, which beats v-show’s inline display:none
            and leaves every tab’s content visible. Wrap so v-show is on a plain
            block; inner layout keeps d-flex.
        -->
        <template v-for="tab in tabs" :key="tab.value">
            <div
                v-show="activeTab === tab.value"
                class="w-100"
                :data-tab="tab.value"
            >
                <div class="d-flex flex-column">
                    <Field
                        v-if="sectionUsesSubsections(tab.value)"
                        class="w-100 mt-3"
                        label="Section:"
                        label-position="left"
                        type="select"
                        v-model="activeSubsectionBySection[tab.value]"
                        :params="subsectionSelectParams(tab.value)"
                    />
                    <div
                        v-if="sectionUsesSubsections(tab.value)"
                        class="subsection-nav-divider mt-3"
                    ></div>
                    <div
                        class="d-flex flex-column mt-4"
                        style="gap: var(--space-5)"
                        :data-subsection="
                            sectionUsesSubsections(tab.value)
                                ? activeSubsectionBySection[tab.value]
                                : '0'
                        "
                    >
                        <div
                            v-for="(field, fieldIdx) in getDisplayFieldsForTab(
                                tab.value
                            )"
                            :key="field.id ?? `field-${tab.value}-${fieldIdx}`"
                            style="padding-left: 2px"
                        >
                            <FieldBuilder
                                :field="field"
                                :isBuilding="false"
                                :disabled="!canEdit"
                                :panel-id="panelId"
                                :expand-observation-evidence="
                                    shouldForceExpandObservation(
                                        tab.value,
                                        field,
                                        fieldIdx
                                    )
                                "
                                @update:field="updateAudit"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </form>
</template>

<script>
import Field from '../Field.vue';
import FieldBuilder from '../FieldBuilder.vue';
import TabGroup from '../TabGroup.vue';
import SiteDropdownField from '../fields/SiteDropdownField.vue';
import { useSiteStore } from '@/stores/site';
import { useAreaStore } from '@/stores/area';
import { getDefaultSitesForNewRecord } from '@/utils/SiteAccessUtils';
import { calculateScoreForFields } from '@/utils/auditFormScoring';
import {
    getAllFieldsForSection,
    getSectionMode,
} from '@/utils/formJsonSections';

export default {
    name: 'AuditTemplateContent',
    components: {
        Field,
        FieldBuilder,
        TabGroup,
        SiteDropdownField,
    },
    props: {
        audit: {
            type: Object,
            default: null,
        },
        canEdit: {
            type: Boolean,
            default: true,
        },
        userOptions: {
            type: Array,
            required: true,
        },
        panelId: {
            type: [Number, String],
            default: null,
        },
        startInPerformMode: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            panelType: 'select',
            localAudit: {
                name: '',
                description: '',
                company_id: null,
                date: null,
                auditor_id: this.defaultAuditorId(),
                site_id: null,
                area_id: null,
                template_id: null,
                audit_type: 'AUDIT',
            },
            auditId: null,
            auditTypes: [],
            auditAreas: [],
            areaStore: null,
            auditSites: [],
            siteStore: null,
            isLoadingAuditTemplates: false,
            isLoadingTemplateOptions: false,
            isLoadingAuditTemplate: false,
            fieldsBySection: {
                section1: [],
            },
            fieldsBySubsection: {},
            subsectionOrder: {},
            subsectionTitles: {},
            activeSubsectionBySection: {},
            tabs: [
                {
                    text: 'Section 1',
                    value: 'section1',
                    isEditing: false,
                    hasError: false,
                },
            ],
            activeTab: 'section1',
            modelValidationFocus: null,
        };
    },
    emits: ['submit', 'started', 'areas'],
    computed: {
        isAdminRoute() {
            return this.$route.matched.some((r) => r.name === 'Admin');
        },
        /** admin panel saves must mark consultant-led audits. */
        isAuditAdminSaveRoute() {
            return (
                this.$route.name === 'Admin' ||
                this.$route.name === 'AdminPanel' ||
                this.$route.name === 'Company Detail' ||
                this.$route.matched.some((r) => r.name === 'Admin')
            );
        },
        today() {
            return new Date();
        },
        filteredAuditTypes() {
            const { audit_type } = this.localAudit;
            if (!audit_type) return this.auditTypes;

            return this.auditTypes.filter(
                ({ supported_audit_types }) =>
                    supported_audit_types &&
                    supported_audit_types === audit_type
            );
        },
        areaOptions() {
            if (this.auditAreas?.length > 0) {
                return this.auditAreas;
            }

            if (this.areaStore) {
                return (this.areaStore?.getAreaOptions || []).map((opt) => ({
                    label: opt.label ?? opt.text,
                    value: opt.value,
                }));
            }
            return [];
        },
        siteOptions() {
            const storeOptions = this.siteStore?.getSiteOptions || [];
            const auditIds = new Set(
                this.auditSites?.map((s) => Number(s.value)) || []
            );
            const userIds = new Set(
                this.$siteStore?.userSiteIds?.map((id) => Number(id)) || []
            );

            return storeOptions.filter((opt) => {
                const id = Number(opt.value);

                // must be in auditSites if any are defined
                const auditOk = auditIds.size === 0 || auditIds.has(id);

                // must be in userSites if not admin and any are defined
                const userOk =
                    this.isAdminRoute || userIds.size === 0 || userIds.has(id);

                return auditOk && userOk;
            });
        },
        // Default site ID based on view type (matches ScheduleAuditForm pattern)
        defaultSiteId() {
            // For existing audits, use their site_id
            if (this.audit?.site_id) {
                return this.audit.site_id;
            }

            // For new audits, use view-based defaults
            const defaultSites = getDefaultSitesForNewRecord({
                viewType: this.$siteStore?.viewType,
                globalSiteId: this.$siteStore?.globalSiteId,
                userSites: this.$siteStore?.userSiteIds || [],
            });

            // Return first site (single-select)
            return defaultSites.length > 0 ? defaultSites[0] : '';
        },
        // Determine if site field should be disabled
        isSiteFieldDisabled() {
            // Disable in selected-site view (library pattern)
            if (this.$siteStore?.isSelectedSiteView) {
                return true;
            }
            // Disable if only one or no sites available
            return this.siteOptions.length <= 1;
        },
        disabledSiteTooltip() {
            if (this.$siteStore?.isSelectedSiteView) {
                return 'To edit the site, change the view from site view to global view';
            }
            if (this.siteOptions.length === 1) {
                return 'Only one site available';
            }
            return '';
        },
        isTemplatePrefill() {
            return !!(
                this.audit &&
                Object.keys(this.audit).length > 0 &&
                this.audit.template_id &&
                !this.audit.id
            );
        },
        isTemplateSelectionLoading() {
            return (
                this.isLoadingAuditTemplates ||
                this.isLoadingTemplateOptions ||
                this.isLoadingAuditTemplate
            );
        },
        templateSelectionLoadingText() {
            if (this.isLoadingAuditTemplate) {
                return 'Loading template...';
            }

            if (this.isLoadingTemplateOptions) {
                return 'Loading template details...';
            }

            return 'Loading templates...';
        },
        isAuditStartDisabled() {
            return this.isTemplateSelectionLoading || !this.localAudit.template_id;
        },
    },
    methods: {
        defaultAuditorId() {
            const id = this.$userStore?.user?.id;
            return id == null ? null : String(id);
        },
        normalizeObservationFieldFlags(field) {
            const normalized = { ...field };
            const observationAndRecommendationEnabled =
                normalized?.observationAndRecommendation === true;
            const evidenceReviewedEnabled = observationAndRecommendationEnabled
                ? typeof normalized?.evidenceReviewedEnabled === 'boolean'
                    ? normalized.evidenceReviewedEnabled
                    : false
                : false;

            normalized.observationAndRecommendation =
                observationAndRecommendationEnabled;
            normalized.evidenceReviewedEnabled = evidenceReviewedEnabled;

            if (!observationAndRecommendationEnabled) {
                delete normalized.observation;
                delete normalized.recommendation;
                delete normalized.evidenceReviewed;
            } else if (!evidenceReviewedEnabled) {
                delete normalized.evidenceReviewed;
            }

            return normalized;
        },
        sectionUsesSubsections(sectionKey) {
            const order = this.subsectionOrder[sectionKey];
            return Array.isArray(order) && order.length >= 2;
        },
        subsectionSelectParams(tabValue) {
            if (!this.sectionUsesSubsections(tabValue)) return {};
            const order = this.subsectionOrder[tabValue] || [];
            const titles = this.subsectionTitles[tabValue] || {};
            return {
                options: order.map((k) => ({
                    value: k,
                    label: titles[k] || k,
                })),
                placeholder: 'Select a subsection',
                searchable: true,
                allowEmpty: false,
            };
        },
        getDisplayFieldsForTab(key) {
            if (this.sectionUsesSubsections(key)) {
                const sub =
                    this.activeSubsectionBySection[key] ||
                    this.subsectionOrder[key]?.[0];
                return this.fieldsBySubsection[key]?.[sub] || [];
            }
            return this.fieldsBySection[key] || [];
        },
        openAreaForm() {
            this.$emit('areas');
        },
        backToSelection() {
            this.panelType = 'select';
            // Reload templates if not already loaded
            if (this.auditTypes.length === 0) {
                this.loadAuditTemplates();
            }
        },
        loadAuditTemplates() {
            const params = {
                fields: ['id', 'name', 'supported_audit_types'],
                order_by: { name: 'asc' },
            };

            this.isLoadingAuditTemplates = true;

            return this.$getData('audit/template/read', params).then((response) => {
                if (response?.success) {
                    const templates = response.data || [];
                    this.auditTypes = templates.map((template) => ({
                        label: template.name,
                        value: String(template.id),
                        supported_audit_types: template.supported_audit_types,
                    }));
                    return true;
                } else {
                    this.$alertStore.pushError(response?.message);
                    return false;
                }
            }).finally(() => {
                this.isLoadingAuditTemplates = false;
            });
        },
        loadAuditTemplate() {
            const params = {
                where: { id: Number(this.localAudit.template_id) },
            };

            this.isLoadingAuditTemplate = true;

            return this.$getData('audit/template/read', params).then((response) => {
                if (response?.success && response.data) {
                    const { id, name, description, company_id, form_json } =
                        response.data?.[0];
                    // Calculate total max_score across all sections
                    const totalMaxScore = Array.isArray(form_json)
                        ? form_json.reduce(
                              (sum, page) => sum + Number(page.max_score ?? 0),
                              0
                          )
                        : 0;

                    this.localAudit.name = name || '';
                    this.localAudit.description = description || '';
                    this.localAudit.company_id = company_id ?? null;
                    this.localAudit.form_max_score = totalMaxScore || null;
                    this.populateFromFormJson(form_json);
                    return true;
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to load template'
                    );
                    return false;
                }
            }).finally(() => {
                this.isLoadingAuditTemplate = false;
            });
        },
        loadAuditTemplateOptions() {
            const params = {
                where: { id: Number(this.localAudit.template_id) },
                fields: ['id'],
                include: {
                    sites: {
                        fields: ['id', 'name'],
                    },
                    areas: {
                        fields: ['id', 'name'],
                    },
                },
            };

            this.isLoadingTemplateOptions = true;

            return this.$getData('audit/template/read', params).then((response) => {
                if (response?.success && response.data) {
                    const { id, sites, areas } = response.data?.[0];

                    const siteOptions =
                        sites?.map((s) => ({
                            label: s.name,
                            value: String(s.id),
                        })) || [];

                    const areaOptions =
                        areas?.map((a) => ({
                            label: a.name,
                            value: String(a.id),
                        })) || [];

                    this.auditSites = siteOptions;
                    this.auditAreas = areaOptions;
                    return true;
                } else {
                    this.$alertStore.pushError(
                        response?.message || 'Failed to load template'
                    );
                    return false;
                }
            }).finally(() => {
                this.isLoadingTemplateOptions = false;
            });
        },
        populateFromFormJson(form_json) {
            this.fieldsBySubsection = {};
            this.subsectionOrder = {};
            this.subsectionTitles = {};
            this.activeSubsectionBySection = {};
            this.tabs = form_json.map((section, index) => ({
                text: section.heading,
                value: `section${index + 1}`,
                isEditing: false,
                hasError: false,
            }));
            this.fieldsBySection = {};
            form_json.forEach((section, index) => {
                const key = `section${index + 1}`;
                if (getSectionMode(section) === 'subsections') {
                    const subs = section.subsections || [];
                    if (subs.length >= 2) {
                        this.fieldsBySubsection[key] = {};
                        this.subsectionOrder[key] = [];
                        this.subsectionTitles[key] = {};
                        subs.forEach((sub, subIndex) => {
                            const subKey = `sub-${subIndex}`;
                            this.subsectionOrder[key].push(subKey);
                            this.subsectionTitles[key][subKey] =
                                sub.heading || `Subsection ${subIndex + 1}`;
                            this.fieldsBySubsection[key][subKey] = (
                                sub.fields || []
                            ).map((field) => {
                                const normalizedField =
                                    this.normalizeObservationFieldFlags(field);
                                return {
                                    ...normalizedField,
                                    isEditing: false,
                                };
                            });
                        });
                        this.activeSubsectionBySection[key] =
                            this.subsectionOrder[key][0] || null;
                        this.fieldsBySection[key] = [];
                    } else {
                        this.fieldsBySection[key] = (subs[0]?.fields || []).map(
                            (field) => {
                                const normalizedField =
                                    this.normalizeObservationFieldFlags(field);
                                return {
                                    ...normalizedField,
                                    isEditing: false,
                                };
                            }
                        );
                    }
                } else {
                    this.fieldsBySection[key] = (section.fields || []).map(
                        (field) => {
                            const normalizedField =
                                this.normalizeObservationFieldFlags(field);
                            return {
                                ...normalizedField,
                                isEditing: false,
                            };
                        }
                    );
                }
            });
            this.activeTab = this.tabs.length ? this.tabs[0].value : '';
        },
        updateAudit(updatedField) {
            for (const sectionKey in this.fieldsBySection) {
                if (this.sectionUsesSubsections(sectionKey)) {
                    for (const subKey of this.subsectionOrder[sectionKey] ||
                        []) {
                        const sectionArray =
                            this.fieldsBySubsection[sectionKey]?.[subKey] || [];
                        const idx = sectionArray.findIndex(
                            (f) => f.id === updatedField.id
                        );
                        if (idx !== -1) {
                            Object.assign(sectionArray[idx], updatedField);
                            return;
                        }
                    }
                } else {
                    const sectionArray = this.fieldsBySection[sectionKey] || [];
                    const idx = sectionArray.findIndex(
                        (f) => f.id === updatedField.id
                    );
                    if (idx !== -1) {
                        Object.assign(sectionArray[idx], updatedField);
                        return;
                    }
                }
            }
        },
        async submitForm() {
            if (this.isAuditStartDisabled) {
                return;
            }

            const templateLoaded = await this.loadAuditTemplate();

            if (!templateLoaded) {
                return;
            }

            this.$emit('started', this.localAudit.audit_type);
            this.panelType = 'create';
        },
        mapFieldToCleanJson(field) {
            const normalizedField = this.normalizeObservationFieldFlags(field);
            const { isEditing, ...cleanField } = normalizedField;
            if (Array.isArray(cleanField.conditionals)) {
                cleanField.conditionals.forEach((cond) => {
                    if (
                        cleanField.value !== cond.value &&
                        cond.field?.value !== undefined
                    ) {
                        cond.field.value = '';
                    }
                });
            }
            if (
                cleanField.recommendation != null &&
                Array.isArray(cleanField.scoring_rules) &&
                cleanField.scoring_rules.length > 0
            ) {
                const values = Array.isArray(cleanField.value)
                    ? cleanField.value
                    : [cleanField.value];
                const isCorrect = values.some((v) =>
                    cleanField.scoring_rules.includes(v)
                );
                if (isCorrect) {
                    delete cleanField.recommendation;
                }
            }
            return cleanField;
        },
        getFormattedFormJson() {
            return this.tabs.map((tab) => {
                const key = tab.value;
                if (this.sectionUsesSubsections(key)) {
                    const subsections = (this.subsectionOrder[key] || []).map(
                        (subKey) => ({
                            heading:
                                this.subsectionTitles[key]?.[subKey] ||
                                'Subsection',
                            fields: (
                                this.fieldsBySubsection[key]?.[subKey] || []
                            ).map((field) =>
                                this.mapFieldToCleanJson({ ...field })
                            ),
                        })
                    );
                    return {
                        heading: tab.text,
                        fields: [],
                        subsections,
                    };
                }
                return {
                    heading: tab.text,
                    fields: (this.fieldsBySection[key] || []).map((field) =>
                        this.mapFieldToCleanJson({ ...field })
                    ),
                };
            });
        },
        hasMeaningfulValue(value, type) {
            if (Array.isArray(value)) return value.length > 0;
            if (type === 'checkbox') return value === true;
            if (typeof value === 'number') return Number.isFinite(value);
            if (typeof value === 'boolean') return value;
            if (value == null) return false;
            if (typeof value === 'string') return value.trim().length > 0;
            return true;
        },
        getFieldSelections(field) {
            if (field?.type === 'checkbox-group') {
                return Array.isArray(field?.value) ? field.value : [];
            }
            if (field?.type === 'radio') {
                return field?.value != null && field.value !== ''
                    ? [field.value]
                    : [];
            }
            return [];
        },
        isObservationEvidenceField(field) {
            return (
                field?.observationAndRecommendation === true &&
                !!field?.max_score &&
                Array.isArray(field?.params?.options) &&
                (field?.type === 'radio' || field?.type === 'checkbox-group')
            );
        },
        isRecommendationRequired(field) {
            if (!this.isObservationEvidenceField(field)) return false;

            const rules = Array.isArray(field?.scoring_rules)
                ? field.scoring_rules
                : [];
            if (rules.length === 0) return false;

            const options = Array.isArray(field?.params?.options)
                ? field.params.options
                : [];
            const naValues = new Set(
                options.filter((opt) => opt?.isNA).map((opt) => opt.value)
            );

            const selectedScoredValues = this.getFieldSelections(field).filter(
                (v) => !naValues.has(v)
            );
            if (selectedScoredValues.length === 0) return false;

            return selectedScoredValues.some((v) => !rules.includes(v));
        },
        validateRequiredField(field) {
            if (!field || typeof field !== 'object') return true;
            let expandObservationEvidence = false;

            if (
                field.required === true &&
                !this.hasMeaningfulValue(field.value, field.type)
            ) {
                return {
                    valid: false,
                    expandObservationEvidence,
                };
            }

            if (this.isObservationEvidenceField(field)) {
                const hasSelection = this.getFieldSelections(field).length > 0;

                if (
                    hasSelection &&
                    !this.hasMeaningfulValue(field.observation, 'textarea')
                ) {
                    expandObservationEvidence = true;
                    return {
                        valid: false,
                        expandObservationEvidence,
                    };
                }

                if (
                    hasSelection &&
                    field.evidenceReviewedEnabled === true &&
                    !this.hasMeaningfulValue(field.evidenceReviewed, 'text')
                ) {
                    expandObservationEvidence = true;
                    return {
                        valid: false,
                        expandObservationEvidence,
                    };
                }

                if (
                    this.isRecommendationRequired(field) &&
                    !this.hasMeaningfulValue(field.recommendation, 'textarea')
                ) {
                    expandObservationEvidence = true;
                    return {
                        valid: false,
                        expandObservationEvidence,
                    };
                }
            }

            if (Array.isArray(field.conditionals)) {
                for (const cond of field.conditionals) {
                    const condField = cond?.field;
                    if (!condField) continue;

                    const parentSelections = this.getFieldSelections(field);
                    const conditionalVisible =
                        field?.type === 'checkbox-group'
                            ? parentSelections.includes(cond?.value)
                            : field?.value === cond?.value;

                    if (
                        conditionalVisible &&
                        condField.required === true &&
                        !this.hasMeaningfulValue(
                            condField.value,
                            condField.type
                        )
                    ) {
                        return {
                            valid: false,
                            expandObservationEvidence,
                        };
                    }
                }
            }

            return {
                valid: true,
                expandObservationEvidence: false,
            };
        },
        collectModelValidationTargets() {
            const invalidTargets = [];
            this.tabs.forEach((tab) => {
                const key = tab.value;
                if (this.sectionUsesSubsections(key)) {
                    (this.subsectionOrder[key] || []).forEach((subKey) => {
                        const fields =
                            this.fieldsBySubsection[key]?.[subKey] || [];
                        fields.forEach((field, fieldIndex) => {
                            const result = this.validateRequiredField(field);
                            if (!result.valid) {
                                invalidTargets.push({
                                    tabKey: key,
                                    subKey,
                                    fieldId: field?.id ?? null,
                                    fieldIndex,
                                    expandObservationEvidence:
                                        result.expandObservationEvidence ===
                                        true,
                                });
                            }
                        });
                    });
                } else {
                    const fields = this.fieldsBySection[key] || [];
                    fields.forEach((field, fieldIndex) => {
                        const result = this.validateRequiredField(field);
                        if (!result.valid) {
                            invalidTargets.push({
                                tabKey: key,
                                subKey: null,
                                fieldId: field?.id ?? null,
                                fieldIndex,
                                expandObservationEvidence:
                                    result.expandObservationEvidence === true,
                            });
                        }
                    });
                }
            });
            return invalidTargets;
        },
        shouldForceExpandObservation(tabKey, field, fieldIdx) {
            const focus = this.modelValidationFocus;
            if (!focus || !focus.expandObservationEvidence) return false;
            if (focus.tabKey !== tabKey) return false;
            if (focus.fieldId != null && field?.id != null) {
                return focus.fieldId === field.id;
            }
            return focus.fieldIndex === fieldIdx;
        },
        applyModelValidationErrors(invalidTargets) {
            const tabSet = new Set(
                invalidTargets.map((target) => target.tabKey)
            );
            this.tabs.forEach((tab) => {
                tab.hasError = tabSet.has(tab.value);
            });
        },
        focusFirstModelInvalid(invalidTargets) {
            const firstInvalid = invalidTargets[0];
            if (!firstInvalid) return;
            if (firstInvalid.tabKey) {
                this.activeTab = firstInvalid.tabKey;
                if (firstInvalid.subKey != null) {
                    this.activeSubsectionBySection[firstInvalid.tabKey] =
                        firstInvalid.subKey;
                }
            }
            this.modelValidationFocus = {
                tabKey: firstInvalid.tabKey || null,
                subKey: firstInvalid.subKey ?? null,
                fieldId: firstInvalid.fieldId ?? null,
                fieldIndex:
                    typeof firstInvalid.fieldIndex === 'number'
                        ? firstInvalid.fieldIndex
                        : null,
                expandObservationEvidence:
                    firstInvalid.expandObservationEvidence === true,
            };
        },
        handleInvalidInputs() {
            const form = this.$refs.auditForm;
            const firstInvalid = form.querySelector(':invalid');
            if (!firstInvalid) return;

            let el = firstInvalid;
            let tabVal = null;
            let subVal = null;
            while (el && el !== form) {
                if (el.dataset) {
                    if (el.dataset.tab) tabVal = el.dataset.tab;
                    if (
                        el.dataset.subsection != null &&
                        el.dataset.subsection !== ''
                    ) {
                        subVal = el.dataset.subsection;
                    }
                }
                el = el.parentNode;
            }
            if (tabVal) {
                this.activeTab = tabVal;
                if (subVal != null) {
                    this.activeSubsectionBySection[tabVal] = subVal;
                }
            }
            firstInvalid.focus();
        },
        updateTabErrors() {
            const form = this.$refs.auditForm;
            const invalidFields = form.querySelectorAll(':invalid');

            this.tabs.forEach((tab) => {
                tab.hasError = false;
            });

            invalidFields.forEach((field) => {
                let el = field;
                while (el && !el.dataset.tab) {
                    el = el.parentNode;
                }
                if (el && el.dataset.tab) {
                    const tab = this.tabs.find(
                        (t) => t.value === el.dataset.tab
                    );
                    if (tab) tab.hasError = true;
                }
            });
        },
        saveAudit() {
            const form = this.$refs.auditForm;
            this.modelValidationFocus = null;
            const invalidTargets = this.collectModelValidationTargets();

            if (invalidTargets.length > 0) {
                this.applyModelValidationErrors(invalidTargets);
                this.focusFirstModelInvalid(invalidTargets);
                this.$alertStore.pushError(
                    'Please fill in all required fields'
                );
                return;
            }

            if (!form.checkValidity()) {
                this.updateTabErrors();
                form.reportValidity();
                this.handleInvalidInputs();
                return;
            }

            // Calculate total score and max possible across all tabs
            let totalScore = 0;
            let totalMaxScore = 0;

            this.tabs.forEach((tab) => {
                const key = tab.value;
                const sectionPayload = this.sectionUsesSubsections(key)
                    ? {
                          subsections: (this.subsectionOrder[key] || []).map(
                              (subKey) => ({
                                  heading:
                                      this.subsectionTitles[key]?.[subKey] ||
                                      '',
                                  fields:
                                      this.fieldsBySubsection[key]?.[subKey] ||
                                      [],
                              })
                          ),
                      }
                    : { fields: this.fieldsBySection[key] || [] };
                const fields = getAllFieldsForSection(sectionPayload);
                const result = calculateScoreForFields(fields);
                totalScore += result.totalScore;
                totalMaxScore += result.totalMaxPossible;
            });

            const auditData = { ...this.localAudit };
            // Keep explicit company scope for admin portal cross-company updates.
            if (!this.isAuditAdminSaveRoute) {
                delete auditData.company_id;
            }

            const new_audit = {
                ...auditData,
                form_json: this.getFormattedFormJson(),
                form_score: totalScore || 0,
                form_max_score: totalMaxScore || null,
            };

            if (this.auditId) {
                new_audit.id = this.auditId;
            }

            if (!this.audit?.completed_at) {
                new_audit.completed_at = Date.now();
            }

            if (this.isAuditAdminSaveRoute) {
                new_audit.app_admin_only = true;
            }
            const endpoint = this.auditId ? 'audit/update' : 'audit/create';
            this.$postData(endpoint, new_audit).then((response) => {
                if (response?.success) {
                    this.$emit('submit');
                } else {
                    this.$alertStore.pushError(response?.message);
                }
            });
        },
    },
    watch: {
        canEdit(newVal) {
            if (this.auditId) {
                this.panelType = newVal ? 'edit' : 'view';
            }
        },
        siteOptions(newVal) {
            // If no site selected yet and options are available
            if (!this.localAudit.site_id && newVal.length > 0) {
                // Check if defaultSiteId is in available options
                const defaultId = this.defaultSiteId;
                const isDefaultAvailable = newVal.some(
                    (opt) => opt.value === defaultId
                );

                if (isDefaultAvailable) {
                    // Use view-based default if available
                    this.localAudit.site_id = defaultId;
                } else if (newVal.length === 1) {
                    // Otherwise auto-select if only one option
                    this.localAudit.site_id = newVal[0].value;
                }
            }
        },
        areaOptions(newVal) {
            if (newVal.length === 1) {
                this.localAudit.area_id = newVal[0].value;
            }
        },
        'localAudit.template_id'(newVal) {
            if (newVal) {
                if (this.panelType == 'select') {
                    this.localAudit.area_id = null;
                    this.localAudit.site_id = null;
                    this.loadAuditTemplateOptions();
                }
            }
        },
    },
    created() {
        this.siteStore = useSiteStore();
        this.siteStore.syncSites();
        this.areaStore = useAreaStore();
        this.areaStore.syncAreas();
    },
    mounted() {
        if (this.isTemplatePrefill) {
            this.loadAuditTemplates();
            this.localAudit = {
                ...this.localAudit,
                template_id: this.audit.template_id,
                audit_type: this.audit.audit_type || 'AUDIT',
                date:
                    this.audit.date || new Date().toISOString().split('T')[0],
                auditor_id: this.audit.auditor_id || this.defaultAuditorId(),
                site_id: this.audit.site_id || this.localAudit.site_id,
                area_id: this.audit.area_id || this.localAudit.area_id,
            };
            this.panelType = this.startInPerformMode ? 'create' : 'select';
            if (this.startInPerformMode) {
                this.loadAuditTemplate();
            }
        } else if (this.audit && Object.keys(this.audit).length > 0) {
            this.auditId = this.audit.id || null;
            this.localAudit = {
                name: this.audit.name || '',
                description: this.audit.description || '',
                company_id: this.audit.company_id ?? null,
                date: this.audit.date || null,
                auditor_id: this.audit.auditor_id || null,
                site_id: this.audit.site_id || null,
                area_id: this.audit.area_id || null,
                template_id: this.audit.template_id || null,
                audit_type: this.audit.audit_type || 'AUDIT',
                form_max_score: this.audit.form_max_score ?? null,
            };
            if (this.audit?.score_override != null) {
                this.localAudit.score_override = this.audit.score_override;
            } else {
                this.localAudit.score_override = null;
            }
            if (
                !this.isAdminRoute &&
                !this.audit &&
                Object.keys(this.audit).length == 0
            ) {
                this.localAudit.auditor_id = this.$userStore.user_id;
            }
            this.panelType = this.canEdit ? 'edit' : 'view';
            if (this.audit?.form_json) {
                this.populateFromFormJson(this.audit.form_json);
            } else {
                this.loadAuditTemplate();
            }
        } else {
            this.loadAuditTemplates();
            this.localAudit.date = new Date().toISOString().split('T')[0];
        }
    },
};
</script>

<style scoped>
.audit-title {
    background-color: var(--primary-25);
    display: flex;
    padding: var(--Spacing-space-2, 8px) var(--Spacing-space-4, 16px);
    align-items: center;
    gap: 10px;
    align-self: stretch;
}

.sticky-header {
    position: sticky;
    top: 0;
    /* stick to the top of the scroll container */
    background: white;
    margin-top: 1rem;
    z-index: 10;
}

.tab-hidden {
    visibility: hidden;
    position: absolute;
    height: 0;
    overflow: hidden;
}

.edit-area,
.area-edit-link {
    cursor: pointer;
    color: var(--primary-400, #678277);
    font-family: Inter;
    font-size: 0.9375rem;
    font-style: normal;
    font-weight: 600;
    line-height: 1.125rem;
    /* 120% */
    text-decoration-line: underline;
    white-space: nowrap;
}

.form-container {
    gap: var(--space-4);
}

.area-edit-link:hover {
    color: var(--primary-400, #678277);
    background-color: transparent;
}

.area-container {
    display: flex;
    align-items: flex-end;
    gap: 0.75rem;
}

.area-container .area-edit-link {
    margin-bottom: 0.625rem;
}

.subsection-nav-divider {
    width: 100%;
    border-top: 1px solid var(--neutral-600);
}
</style>
